// src/stores/financial.ts
import { defineStore } from "pinia";
import { ref } from "vue";
import { apiClient } from "../utils/api";
import type { DepositResponse } from "../gen/api/v1/api_pb";
import {
  DepositService,
  PixKeyService,
  WithdrawalService,
} from "../gen/api/v1/api_pb";
import { useAuthStore } from "./auth";

/* Helpers */

// transforma um objeto em AsyncIterable com um único yield
function asStream<T extends Record<string, unknown>>(obj: T): AsyncIterable<T> {
  return (async function* () {
    yield obj;
  })();
}

// detecta stream
function isAsyncIterable<T = unknown>(val: unknown): val is AsyncIterable<T> {
  return !!val && typeof (val as any)[Symbol.asyncIterator] === "function";
}

// normaliza mensagem de erro
function errMsg(e: unknown, fallback: string) {
  return e instanceof Error ? e.message : fallback;
}

/* Tipos simples para a UI (ajuste conforme seu proto) */
type PixKey = {
  id?: number | string;
  key?: string;
  type?: string;
};

export const useFinancialStore = defineStore("financial", () => {
  // --- STATE ---
  const pixKeys = ref<PixKey[]>([]);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const successMessage = ref<string | null>(null);
  const depositResponse = ref<DepositResponse | null>(null);

  const authStore = useAuthStore();

  /**
   * Lista chaves PIX do usuário
   */
  async function fetchPixKeys() {
    loading.value = true;
    error.value = null;

    try {
      if (!authStore.user) throw new Error("Usuário não autenticado.");

      const req = { userId: authStore.user.id } as Record<string, unknown>;
      const resp = await apiClient(PixKeyService).listPixKeys(
        asStream(req) as any,
      );

      const acc: PixKey[] = [];

      if (isAsyncIterable(resp)) {
        for await (const msg of resp as AsyncIterable<any>) {
          if (Array.isArray(msg?.keys)) acc.push(...msg.keys);
          else if (msg?.key) acc.push(msg as PixKey);
          else void msg;
        }
        pixKeys.value = acc;
      } else {
        const msg = resp as any;
        pixKeys.value = Array.isArray(msg?.keys) ? msg.keys : [];
      }
    } catch (e) {
      console.error("Erro ao buscar chaves PIX:", e);
      error.value = errMsg(e, "Não foi possível carregar as suas chaves PIX.");
    } finally {
      loading.value = false;
    }
  }

  /**
   * Adiciona uma nova chave PIX
   * @param keyData { key: string; type: string } // type em MAIÚSCULO
   */
  async function addPixKey(keyData: { key: string; type: string }) {
    loading.value = true;
    error.value = null;
    successMessage.value = null;
    try {
      const req = {
        key: keyData.key,
        type: keyData.type.toUpperCase(), // "CPF" | "CNPJ" | "PHONE" | "EMAIL" | "RANDOM"
      } as any;

      const resp = await apiClient(PixKeyService).addPixKey(req);

      if ((resp as any)?.[Symbol.asyncIterator]) {
        for await (const _ of resp as AsyncIterable<unknown>) {
          console.log(_);
        }
      }

      successMessage.value = "Chave PIX adicionada com sucesso!";
      await fetchPixKeys(); // recarrega a lista
      return true;
    } catch (e) {
      console.error("Erro ao adicionar chave PIX:", e);
      error.value = errMsg(e, "Não foi possível adicionar a chave PIX.");
      return false;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Solicita um novo saque.
   * @param withdrawalData { amount: number; pixKeyId: number | string }
   */
  async function requestWithdrawal(withdrawalData: {
    amount: number;
    pixKeyId: number | string;
  }) {
    loading.value = true;
    error.value = null;
    successMessage.value = null;

    try {
      if (!authStore.user) throw new Error("Usuário não autenticado.");

      const pixKeyIdNum =
        typeof withdrawalData.pixKeyId === "number"
          ? withdrawalData.pixKeyId
          : Number(withdrawalData.pixKeyId);

      if (!Number.isFinite(pixKeyIdNum)) {
        throw new Error("pixKeyId inválido");
      }

      // ✅ Request FLAT, exatamente como no proto
      const req = {
        amount: Number(withdrawalData.amount),
        pixKeyId: pixKeyIdNum,
        // type: 'PIX', // opcional, se seu backend usar
      } as any;

      // ✅ Unary (sem asStream)
      const resp = await apiClient(WithdrawalService).createWithdrawal(req);

      // Se for server-streaming na resposta, consome:
      if ((resp as any)?.[Symbol.asyncIterator]) {
        for await (const _ of resp as AsyncIterable<unknown>) {
          console.log(_);
        }
      }

      successMessage.value = "Pedido de saque enviado com sucesso!";
      return true;
    } catch (e) {
      console.error("Erro ao solicitar saque:", e);
      error.value = errMsg(
        e,
        "Não foi possível processar o seu pedido de saque.",
      );
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Limpa mensagens
  function clearMessages() {
    error.value = null;
    successMessage.value = null;
  }

  /**
   * Cria um novo pedido de depósito e retorna os dados do PIX.
   */
  async function createDeposit(amount: number) {
    loading.value = true;
    error.value = null;
    depositResponse.value = null;

    try {
      if (!authStore.user) throw new Error("Usuário não autenticado.");

      const depositMessage = {
        deposit: {
          amount,
          userId: authStore.user.id,
        },
      } as Record<string, unknown>;

      const resp = await apiClient(DepositService).createDeposit(
        asStream(depositMessage) as any,
      );

      if (isAsyncIterable<DepositResponse>(resp)) {
        let last: DepositResponse | null = null;
        for await (const msg of resp) {
          last = msg;
        }
        depositResponse.value = last;
      } else {
        depositResponse.value = resp as DepositResponse;
      }

      return true;
    } catch (e) {
      console.error("Erro ao criar depósito:", e);
      error.value = errMsg(e, "Não foi possível iniciar o depósito.");
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Limpa a resposta do depósito para fechar o modal do PIX
  function clearDepositResponse() {
    depositResponse.value = null;
  }

  return {
    pixKeys,
    loading,
    error,
    successMessage,
    depositResponse,
    fetchPixKeys,
    addPixKey,
    requestWithdrawal,
    clearMessages,
    createDeposit,
    clearDepositResponse,
  };
});
